import React, { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import ExpandableEventCard from '../components/ExpandableEventCard';
import RecentActivityList from '../components/RecentActivityList';
import IncomingCards from '../components/IncomingCards';
import { subscribeTLDRsWithEmails, deleteTLDR } from '../services/tldrService';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';
import { useFamilyRole } from '../hooks/useFamilyRole';
import { useFirestoreUser } from '../hooks/useFirestoreUser';
import { useFamilyParticipants } from '../hooks/useFamilyParticipants';


// Helper function to extract title from TLDR summary
const extractTitleFromSummary = (summary) => {
  if (!summary) return '';
  
  // Look for the first line that ends with a colon (typical TLDR format)
  const lines = summary.split('\n');
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.endsWith(':') && trimmed.includes('Update') || trimmed.includes('Week')) {
      // Remove markdown formatting and colon
      return trimmed.replace(/\*\*/g, '').replace(/:$/, '');
    }
  }
  
  // Fallback: return the first line or first 60 characters
  const firstLine = lines[0]?.trim().replace(/\*\*/g, '') || '';
  return firstLine.length > 60 ? firstLine.substring(0, 60) + '...' : firstLine;
};

// Helper function to format Markdown text to HTML
const formatMarkdownToHTML = (text) => {
  if (!text) return '';
  
  // Find "Upcoming Events:" and split the text
  const upcomingEventsIndex = text.indexOf('Upcoming Events:');
  let beforeUpcoming = text;
  let afterUpcoming = '';
  
  if (upcomingEventsIndex !== -1) {
    beforeUpcoming = text.substring(0, upcomingEventsIndex);
    afterUpcoming = text.substring(upcomingEventsIndex);
  }
  
  // Process the part before "Upcoming Events:" with full formatting
  const formattedBefore = beforeUpcoming
    // Bold text: **text** -> <strong>text</strong>
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic text: *text* -> <em>text</em>
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Convert line breaks and bullet points
    .split('\n')
    .map(line => {
      const trimmedLine = line.trim();
      // Skip completely empty lines
      if (!trimmedLine) return null;
      
      // Handle bullet points but skip "Upcoming Events:" and empty content
      if (trimmedLine.startsWith('- ')) {
        let content = trimmedLine.substring(2).trim();
        if (!content || content === '') {
          return null; // Skip empty bullet points completely
        }
        if (content === 'Upcoming Events:') {
          return `<p><strong>${content}</strong></p>`;
        }
        
        // Apply formatting to content first
        content = content
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*(.*?)\*/g, '<em>$1</em>');
          
        // Then check if it's actually empty after processing
        const cleanContent = content
          .replace(/<em><\/em>/g, '') // Remove empty em tags
          .replace(/<strong><\/strong>/g, '') // Remove empty strong tags
          .trim();
        if (!cleanContent) {
          return null; // Skip if content is empty after cleaning
        }
        return `<li>${content}</li>`;
      }
      // Handle regular lines
      return `<p>${trimmedLine}</p>`;
    })
    .filter(element => element !== null && element.trim() !== '') // Remove null and empty elements
    .join('')
    // Wrap consecutive <li> elements in <ul>
    .replace(/(<li>.+?<\/li>)(\s*<li>.+?<\/li>)*/g, (match) => {
      return `<ul>${match}</ul>`;
    });
    
  // Process the part after "Upcoming Events:" with basic formatting only (no bullet points)
  const formattedAfter = afterUpcoming
    // Bold text: **text** -> <strong>text</strong>
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic text: *text* -> <em>text</em>
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Convert line breaks but keep original formatting for events
    .split('\n')
    .map(line => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return '';
      // Remove leading dashes but keep emojis and other characters
      const cleanedLine = trimmedLine.startsWith('- ') ? trimmedLine.substring(2) : trimmedLine;
      // Make "Upcoming Events:" bold
      if (cleanedLine === 'Upcoming Events:' || cleanedLine.includes('Upcoming Events:')) {
        return `<p><strong>Upcoming Events:</strong></p>`;
      }
      return `<p>${cleanedLine}</p>`;
    })
    .join('')
    // Clean up empty paragraphs
    .replace(/<p><\/p>/g, '');
  
  return formattedBefore + formattedAfter;
};

// Helper function to clean up encoding issues in email content
const cleanEmailContent = (text) => {
  if (!text) return text;
  
  let cleanedText = text;
  
  // First, handle HTML content by stripping tags and converting to plain text
  // Check if the content looks like HTML
  if (text.includes('<') && text.includes('>')) {
    // Remove common HTML tags but keep their content
    cleanedText = cleanedText
      // Convert line breaks to newlines
      .replace(/<br\s*\/?>/gi, '\n')
      // Handle paragraphs - add double newline after closing p tag
      .replace(/<\/p>/gi, '\n\n')
      // Remove opening p tags
      .replace(/<p[^>]*>/gi, '')
      // Handle divs - add newline after closing div
      .replace(/<\/div>/gi, '\n')
      // Remove opening div tags
      .replace(/<div[^>]*>/gi, '')
      // Handle list items
      .replace(/<\/li>/gi, '\n')
      .replace(/<li[^>]*>/gi, '• ')
      // Handle headings
      .replace(/<\/h[1-6]>/gi, '\n\n')
      .replace(/<h[1-6][^>]*>/gi, '')
      // Remove other HTML tags but keep content
      .replace(/<[^>]+>/g, '')
      // Decode HTML entities
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ')
      .replace(/&hellip;/g, '...')
      .replace(/&mdash;/g, '—')
      .replace(/&ndash;/g, '–')
      .replace(/&ldquo;/g, '"')
      .replace(/&rdquo;/g, '"')
      .replace(/&lsquo;/g, "'")
      .replace(/&rsquo;/g, "'");
  }
  
  cleanedText = cleanedText
    // Fix smart quotes and apostrophes
    .replace(/â€™/g, "'")
    .replace(/â€œ/g, '"')
    .replace(/â€/g, '"')
    .replace(/â€˜/g, "'")
    .replace(/â€Œ/g, "")
    .replace(/â€‹/g, "")
    // Fix em dash and en dash
    .replace(/â€"/g, "—")
    .replace(/â€"/g, "–")
    // Fix other common encoding issues
    .replace(/Ã¡/g, "á")
    .replace(/Ã©/g, "é")
    .replace(/Ã­/g, "í")
    .replace(/Ã³/g, "ó")
    .replace(/Ãº/g, "ú")
    .replace(/Ã±/g, "ñ")
    .replace(/Â/g, "")
    .replace(/â/g, "")
    // Fix specific patterns like "Arborć s" to "Arbor's"
    .replace(/(\w)ć\s+s/g, "$1's")
    .replace(/(\w)ć\s+t/g, "$1't")
    .replace(/(\w)ć\s+re/g, "$1're")
    .replace(/(\w)ć\s+ll/g, "$1'll")
    .replace(/(\w)ć\s+ve/g, "$1've")
    .replace(/(\w)ć\s+d/g, "$1'd")
    // Remove standalone weird characters that are likely encoding artifacts
    .replace(/ć/g, "'")
    .replace(/â€/g, "")
    .replace(/Â/g, "")
    // Clean up any remaining weird characters
    .replace(/[^\x00-\x7F]/g, (char) => {
      // Keep common punctuation and letters, replace weird ones
      const code = char.charCodeAt(0);
      if (code === 8217) return "'"; // Right single quotation mark
      if (code === 8216) return "'"; // Left single quotation mark  
      if (code === 8220) return '"'; // Left double quotation mark
      if (code === 8221) return '"'; // Right double quotation mark
      if (code === 8211) return "–"; // En dash
      if (code === 8212) return "—"; // Em dash
      return ""; // Remove other weird characters instead of keeping them
    });

  // Remove email forwarding headers and metadata
  cleanedText = cleanedText
    // Remove forwarded message headers
    .replace(/^-{5,}\s*Forwarded message\s*-{5,}[\s\S]*?(?=^[A-Za-z])/gm, '')
    // Remove Gmail-style forwarded headers
    .replace(/^From:.*?$\n?/gm, '')
    .replace(/^Date:.*?$\n?/gm, '')
    .replace(/^Subject:.*?$\n?/gm, '')
    .replace(/^To:.*?$\n?/gm, '')
    .replace(/^Cc:.*?$\n?/gm, '')
    .replace(/^Bcc:.*?$\n?/gm, '')
    // Remove Seesaw images and links more aggressively
    .replace(/\[image:\s*[^\]]*\]/gi, '')
    .replace(/View\s+[^\n]*?Message\s*/gi, '')
    .replace(/<https?:\/\/[^>\s]*>/g, '')
    // Remove various Seesaw footer patterns
    .replace(/Notification Preferences[\s\S]*?$/gmi, '')
    .replace(/Download the App[\s\S]*?$/gmi, '')
    .replace(/Help\s*[\s\S]*?$/gmi, '')
    .replace(/Seesaw Learning, Inc\.[\s\S]*?$/gmi, '')
    .replace(/548 Market Street[\s\S]*?$/gmi, '')
    .replace(/PMB \d+[\s\S]*?$/gmi, '')
    .replace(/You are receiving this email[\s\S]*?$/gmi, '')
    .replace(/or have been invited by a teacher[\s\S]*?$/gmi, '')
    .replace(/Unsubscribe from these emails[\s\S]*?$/gmi, '')
    // Remove pipes and vertical bars that are part of email formatting
    .replace(/^\s*\|\s*$/gm, '')
    .replace(/\s*\|\s*/g, ' ')
    // Remove lines that are just whitespace or punctuation
    .replace(/^\s*[,.\-|]+\s*$/gm, '')
    // Remove multiple consecutive newlines
    .replace(/\n{3,}/g, '\n\n')
    // Remove leading/trailing whitespace
    .trim();

  // Additional cleanup for better paragraph handling
  // First, normalize different types of line breaks
  cleanedText = cleanedText
    // Ensure we have consistent line breaks
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');
  
  // Now handle paragraph detection more intelligently
  const lines = cleanedText.split('\n');
  const paragraphs = [];
  let currentParagraph = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    const nextLine = i < lines.length - 1 ? lines[i + 1].trim() : '';
    
    if (line.length === 0) {
      // Empty line - end current paragraph
      if (currentParagraph.length > 0) {
        paragraphs.push(currentParagraph.join(' '));
        currentParagraph = [];
      }
    } else if (
      // Check for natural paragraph breaks
      line.endsWith('.') && nextLine.length > 0 && /^[A-Z]/.test(nextLine) ||
      line.endsWith('!') && nextLine.length > 0 && /^[A-Z]/.test(nextLine) ||
      line.endsWith('?') && nextLine.length > 0 && /^[A-Z]/.test(nextLine) ||
      line.endsWith(':') ||
      line.match(/^(Dear|Hi|Hello|Good\s+(morning|afternoon|evening))/i) ||
      line.match(/^(Sincerely|Best|Regards|Thanks|Thank you)/i) ||
      line.length < 20 && nextLine.length > 0 // Short lines often indicate breaks
    ) {
      currentParagraph.push(line);
      if (currentParagraph.length > 0) {
        paragraphs.push(currentParagraph.join(' '));
        currentParagraph = [];
      }
    } else {
      currentParagraph.push(line);
    }
  }
  
  // Don't forget the last paragraph
  if (currentParagraph.length > 0) {
    paragraphs.push(currentParagraph.join(' '));
  }
  
  return paragraphs.join('\n\n');
};

// CSS for pulsing animation
const pulsingAnimation = `
  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.7);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(40, 167, 69, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(40, 167, 69, 0);
    }
  }
  
  .pulse-animation {
    animation: pulse 2s infinite !important;
  }
`;

function New({
  acceptedEvents,
  pendingEvents,
  acceptEvent,
  ignoreEvent,
  deleteAcceptedEvent,
  updateAcceptedEvent,
  getCategoryStyles,
  formatDateTime,
  isMobile,
  setEventToEdit,
  sharedUserIds
}) {
  const { currentUser, logout } = useAuth();
  const { firestoreUser } = useFirestoreUser();
  const { isChild, isDriver } = useFamilyRole();
  const { participants } = useFamilyParticipants();
  const navigate = useNavigate();
  const [recentActivity, setRecentActivity] = useState([]);
  const [tldrs, setTldrs] = useState([]);
  const [tldrDuplicationMetadata, setTldrDuplicationMetadata] = useState({ duplicatesFound: 0, details: [] });
  const [activeMobileTab, setActiveMobileTab] = useState('events');
  const [showEmptyState, setShowEmptyState] = useState(false);
  
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showFeedbackDropdown, setShowFeedbackDropdown] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackType, setFeedbackType] = useState('');
  const [feedbackForm, setFeedbackForm] = useState({
    type: '',
    subject: '',
    message: '',
    email: ''
  });
  const [heroImage, setHeroImage] = useState(localStorage.getItem('customHeroImage') || '/mainhero.jpeg');
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  // Handle feedback modal
  const openFeedbackModal = (type) => {
    setFeedbackForm({
      type: type,
      subject: '',
      message: '',
      email: currentUser?.email || ''
    });
    setShowFeedbackModal(true);
    setShowFeedbackDropdown(false);
    setShowSettingsMenu(false);
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    try {
      // Save feedback to Firestore
      await addDoc(collection(db, 'feedback'), {
        type: feedbackForm.type,
        subject: feedbackForm.subject,
        message: feedbackForm.message,
        userEmail: feedbackForm.email,
        userId: currentUser?.uid || 'anonymous',
        submittedAt: new Date().toISOString(),
        status: 'new'
      });
      
      // Close modal and reset form
      setShowFeedbackModal(false);
      setFeedbackForm({
        type: '',
        subject: '',
        message: '',
        email: ''
      });
      
      alert('Thank you for your feedback! We have received your message and will get back to you soon.');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Error submitting feedback. Please try again.');
    }
  };

  // Handle showing empty state with delay
  useEffect(() => {
    let timeoutId;
    
    if (pendingEvents.length === 0) {
      // Show empty state after 1.5 seconds if still no events
      timeoutId = setTimeout(() => {
        setShowEmptyState(true);
      }, 1500);
    } else {
      // Hide empty state immediately when events arrive
      setShowEmptyState(false);
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [pendingEvents.length]);

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSettingsMenu && !event.target.closest('.settings-dropdown')) {
        setShowSettingsMenu(false);
        setShowFeedbackDropdown(false); // Also close feedback dropdown
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSettingsMenu]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Handle hero image upload
  const handleHeroImageUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e.target.result;
        setHeroImage(imageUrl);
        localStorage.setItem('customHeroImage', imageUrl);
      };
      reader.readAsDataURL(file);
    } else {
      alert('Please select a valid image file');
    }
  };

  // Consolidated base styles for consistent spacing
  const baseStyles = {
    container: {
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      backgroundColor: '#f5f5f5',
      padding: '20px',
      margin: '0',
      border: 'none',
      borderRadius: '0',
    },
    
    section: {
      marginBottom: '24px',
      width: '100%',
    },
    
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
      padding: '0 20px',
    },
    
    sectionHeading: {
      fontSize: '1.1rem',
      fontWeight: '600',
      margin: '0',
      padding: '0',
    },
    
    eventsList: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      padding: '20px',
      margin: '0',
      maxHeight: 'none',
      backgroundColor: 'transparent',
    }
  };
  
  // Combine pending events and accepted events to show recent activity
  useEffect(() => {
    // Check for duplicates in the accepted events
    const eventWithDuplicateChecks = [...acceptedEvents].map(event => {
      // If already marked as duplicate, keep that flag
      if (event.isDuplicate) return event;
      
      // Otherwise, check for duplicates by ID and firestoreId
      const otherEvents = acceptedEvents.filter(e => {
        // Exclude the current event from comparison
        if (e.id === event.id) return false;
        
        // Also check firestoreId for duplicates
        if (e.firestoreId && event.firestoreId && e.firestoreId === event.firestoreId) {
          return false;
        }
        
        // Only consider events from the same user as potential duplicates
        // If either event doesn't have a userId, skip this check
        if (e.userId && event.userId && e.userId !== event.userId) {
          return false; // Different users, not a duplicate
        }
        
        return true;
      });
      const isDuplicate = checkForDuplication(event, otherEvents);
      
      return {
        ...event,
        isDuplicate
      };
    });
    
    // Get the 10 most recent accepted events
    const recentAccepted = [...eventWithDuplicateChecks]
      .sort((a, b) => {
        // Ensure we have valid dates for sorting
        const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
        const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
        
        // Handle invalid dates
        return (isNaN(dateB.getTime()) ? 0 : dateB.getTime()) - 
               (isNaN(dateA.getTime()) ? 0 : dateA.getTime());
      })
      .slice(0, 10)
      .map(event => {
        // Determine the event source
        let eventSource = 'manual';
        
        if (event.source === 'email' || event.emailSubject) {
          eventSource = 'email';
        } else if (event.source === 'email_attachment') {
          eventSource = 'email attachment';
        } else if (event.googleId || event.source === 'google_calendar') {
          eventSource = 'gcal';
        } else if (event.source === 'paste') {
          eventSource = 'paste';
        } else if (event.source === 'document' || event.source === 'pdf') {
          eventSource = 'document';
        }
        
        // Determine activity type (added or edited)
        let activityType = 'added';
        if (event.lastModified && event.created) {
          try {
            const lastModified = new Date(event.lastModified);
            const created = new Date(event.created);
            
            if (!isNaN(lastModified.getTime()) && !isNaN(created.getTime()) && 
                lastModified.getTime() > created.getTime()) {
              activityType = 'edited';
            }
          } catch (e) {
            // If there's an error parsing dates, default to 'added'
            console.log('Error parsing dates for event', event.id);
          }
        }
        
        // Ensure we have a valid timestamp
        let timestamp;
        try {
          // Try lastModified first
          if (event.lastModified) {
            const lastModified = new Date(event.lastModified);
            if (!isNaN(lastModified.getTime())) {
              timestamp = event.lastModified;
            }
          }
          
          // If not valid, try createdAt
          if (!timestamp && event.createdAt) {
            const createdAt = new Date(event.createdAt);
            if (!isNaN(createdAt.getTime())) {
              timestamp = event.createdAt;
            }
          }
          
          // If still not valid, use current time
          if (!timestamp) {
            timestamp = new Date().toISOString();
          }
        } catch (e) {
          // Fallback to current time on any error
          timestamp = new Date().toISOString();
        }
        
        return {
          ...event,
          eventSource,
          activityType,
          timestamp
        };
      });

    // Before combining, ensure every activity has a valid timestamp
    const validatedActivities = recentAccepted.map(activity => {
      // If timestamp is missing or invalid, assign current time
      if (!activity.timestamp || isNaN(new Date(activity.timestamp).getTime())) {
        console.log('Assigning current time to activity with invalid timestamp:', 
                   activity.id, activity.eventName, activity.timestamp);
        return {
          ...activity,
          timestamp: new Date().toISOString() // Use current time as fallback
        };
      }
      return activity;
    });
    
    // Deduplicate events that might appear twice (from local update and Firebase listener)
    const deduplicatedActivities = validatedActivities.reduce((acc, current) => {
      const isDuplicate = acc.some(event => {
        // First check if the users are different
        if (event.userId && current.userId && event.userId !== current.userId) {
          return false; // Different users, not a duplicate even if other fields match
        }
        
        // Check by various ID fields
        return event.id === current.id || 
               (event.firestoreId && current.firestoreId && event.firestoreId === current.firestoreId) ||
               (event.originalId && current.originalId && event.originalId === current.originalId);
      });
      
      if (!isDuplicate) {
        acc.push(current);
      }
      return acc;
    }, []);
    
    // Combine and sort all activity by timestamp
    const allActivity = [...deduplicatedActivities]
      .sort((a, b) => {
        // Ensure we have valid dates for sorting
        const dateA = a.timestamp ? new Date(a.timestamp) : new Date(0);
        const dateB = b.timestamp ? new Date(b.timestamp) : new Date(0);
        
        // Handle invalid dates
        return (isNaN(dateB.getTime()) ? 0 : dateB.getTime()) - 
               (isNaN(dateA.getTime()) ? 0 : dateA.getTime());
      })
      .slice(0, 10); // Show only the 10 most recent activities

    setRecentActivity(allActivity);
  }, [acceptedEvents, pendingEvents]);
  
  // Simplified duplicate detection for UI display
  const checkForDuplication = (event, otherEvents) => {
    if (!otherEvents || otherEvents.length === 0) return false;
    
    // First, ensure we're only comparing events from the same user
    // This is already handled in the filter above, but adding a safety check
    const sameUserEvents = otherEvents.filter(e => {
      // If both events have a userId, they must match to be considered duplicates
      if (e.userId && event.userId) {
        return e.userId === event.userId;
      }
      // If user IDs are missing, proceed with other checks
      return true;
    });
    
    if (sameUserEvents.length === 0) return false;
    
    // Find events on the same day
    const sameDay = sameUserEvents.filter(e => 
      e.day === event.day && 
      e.month === event.month && 
      e.year === event.year
    );
    
    if (sameDay.length === 0) return false;
    
    // Check for time overlap
    for (const otherEvent of sameDay) {
      if (event.hasTime && otherEvent.hasTime) {
        const eventStart = (event.hour * 60) + (event.minute || 0);
        const otherStart = (otherEvent.hour * 60) + (otherEvent.minute || 0);
        
        const eventDuration = event.eventDuration || 60;
        const otherDuration = otherEvent.eventDuration || 60;
        
        const eventEnd = eventStart + eventDuration;
        const otherEnd = otherStart + otherDuration;
        
        const overlap = (eventStart >= otherStart && eventStart < otherEnd) ||
                        (otherStart >= eventStart && otherStart < eventEnd);
        
        if (!overlap) continue;
      }
      
      // Check name similarity
      const name1 = (event.eventName || '').toLowerCase();
      const name2 = (otherEvent.eventName || '').toLowerCase();
      
      // Simple name similarity check
      if (name1 === name2 || 
          name1.includes(name2) || 
          name2.includes(name1) ||
          (name1.length > 3 && name2.length > 3 && compareNames(name1, name2))) {
        return true;
      }
    }
    
    return false;
  };
  
  // Helper to compare names by words
  const compareNames = (name1, name2) => {
    const words1 = name1.split(/\s+/).filter(w => w.length > 2);
    const words2 = name2.split(/\s+/).filter(w => w.length > 2);
    
    if (words1.length === 0 || words2.length === 0) return false;
    
    // Check if they share significant words
    return words1.some(w1 => 
      words2.some(w2 => w1 === w2 || w1.includes(w2) || w2.includes(w1))
    );
  };

  // Subscribe to TLDR updates
  useEffect(() => {
    if (!currentUser) {
      setTldrs([]);
      return;
    }
    
    const unsubscribe = subscribeTLDRsWithEmails((updatedTLDRs, duplicationMetadata) => {
      setTldrs(updatedTLDRs);
      setTldrDuplicationMetadata(duplicationMetadata || { duplicatesFound: 0, details: [] });
    }, currentUser.uid, { 
      limit: 10, 
      sharedUserIds: sharedUserIds || [] 
    }); // Get the 10 most recent TLDRs from current user and shared users
    
    return () => unsubscribe();
  }, [currentUser, sharedUserIds]);
  
  // Format the timestamp for display as "time ago"
  const formatTimestamp = (timestamp) => {
    // Validate the timestamp first
    if (!timestamp || timestamp === 'undefined' || timestamp === 'null') {
      console.log('Invalid timestamp value:', timestamp);
      return 'just now'; // Use 'just now' as fallback
    }
    
    // Try to parse the date
    const date = new Date(timestamp);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.log('Invalid date object from timestamp:', timestamp);
      
      // Try alternative parsing methods
      let parsedDate;
      
      // Try to handle numeric timestamps (Unix epoch)
      if (typeof timestamp === 'number' || /^\d+$/.test(timestamp)) {
        parsedDate = new Date(parseInt(timestamp));
      } 
      // Try ISO format with explicit parsing
      else if (typeof timestamp === 'string') {
        // Extract date parts using regex if it's in a common format
        const dateParts = timestamp.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
        if (dateParts) {
          const [_, year, month, day, hours, minutes, seconds] = dateParts;
          parsedDate = new Date(year, month - 1, day, hours, minutes, seconds);
        }
      }
      
      // If we successfully parsed an alternative date format
      if (parsedDate && !isNaN(parsedDate.getTime())) {
        // Use the parsedDate for calculations below
        return calculateTimeAgo(parsedDate);
      } else {
        // Last resort fallback
        return 'just now';
      }
    }
    
    // Use the helper function to calculate time ago
    return calculateTimeAgo(date);
  };
  
  // Helper function to calculate time ago from a date
  const calculateTimeAgo = (date) => {
    const now = new Date();
    const diffMs = now - date;
    
    // Handle negative time differences (future dates or clock skew)
    if (diffMs < 0) {
      return 'just now';
    }
    
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    // Return time ago in a human-readable format
    if (diffSeconds < 10) {
      return 'just now';
    } else if (diffSeconds < 60) {
      return `${diffSeconds} seconds ago`;
    } else if (diffMinutes === 1) {
      return '1 minute ago';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minutes ago`;
    } else if (diffHours === 1) {
      return '1 hour ago';
    } else if (diffHours < 24) {
      return `${diffHours} hours ago`;
    } else if (diffDays === 1) {
      return 'yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
    } else if (diffMonths === 1) {
      return '1 month ago';
    } else if (diffMonths < 12) {
      return `${diffMonths} months ago`;
    } else if (diffYears === 1) {
      return '1 year ago';
    } else {
      return `${diffYears} years ago`;
    }
  };
  
  // Helper function to format time in "11:15 AM" format
  const formatExactTime = (date) => {
    try {
      // Format hours
      let hours = date.getHours();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12; // Convert 0 to 12 for 12 AM
      
      // Format minutes with leading zero
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${hours}:${minutes} ${ampm}`;
    } catch (e) {
      return 'Recent'; // Fallback
    }
  };


  return (
    <>
      <style>{pulsingAnimation}</style>
      {/* Main page container - full screen with gray background */}
      <div className="page-container" style={{
      backgroundColor: '#f5f5f5',
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100%',
      margin: 0,
      padding: 0,
      boxSizing: 'border-box',
      overflow: 'hidden',
      position: 'relative',
      top: isMobile ? '-45px' : '0'
    }}>
      {/* Settings button and dropdown menu - moved outside hero for proper overlay */}
      <div className="settings-dropdown" style={{
        position: 'absolute',
        top: isMobile ? '100px' : '40px',
        right: isMobile ? '20px' : '40px',
        zIndex: 1000
      }}>
        {/* Settings button */}
        <button
          onClick={() => setShowSettingsMenu(!showSettingsMenu)}
          className={participants && participants.length <= 1 ? 'pulse-animation' : ''}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '10px',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            color: '#000',
            border: 'none',
            borderRadius: '50%',
            width: '44px',
            height: '44px',
            cursor: 'pointer',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s ease',
            position: 'relative'
          }}
          onMouseEnter={(e) => {
            if (!(participants && participants.length <= 1)) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.15)';
            }
          }}
          onMouseLeave={(e) => {
            if (!(participants && participants.length <= 1)) {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.1)';
            }
          }}
        >
          <img
            src="/setting.png"
            alt="Settings"
            style={{
              width: '24px',
              height: '24px',
            }}
          />
        </button>
        
        {/* Dropdown menu */}
        {showSettingsMenu && (
          <div style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '8px',
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            overflow: 'visible',
            minWidth: '200px',
            zIndex: 1001,
          }}>
            <button
              onClick={() => {
                navigate('/family-share');
                setShowSettingsMenu(false);
                setShowFeedbackDropdown(false);
                setShowFeedbackDropdown(false);
              }}
              className={participants && participants.length <= 1 ? 'pulse-animation' : ''}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                padding: '14px 20px',
                backgroundColor: 'transparent',
                color: '#000',
                border: 'none',
                fontSize: '0.95rem',
                fontWeight: '500',
                textAlign: 'left',
                cursor: 'pointer',
                borderRadius: '6px',
                transition: 'background-color 0.2s ease',
                position: 'relative'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flexShrink: 0 }}
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
              Family Setup
            </button>
            
            <button
              onClick={() => {
                navigate('/class-setup');
                setShowSettingsMenu(false);
                setShowFeedbackDropdown(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                padding: '14px 20px',
                backgroundColor: 'transparent',
                color: '#000',
                border: 'none',
                fontSize: '0.95rem',
                fontWeight: '500',
                textAlign: 'left',
                cursor: 'pointer',
                borderRadius: '6px',
                transition: 'background-color 0.2s ease',
                position: 'relative'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flexShrink: 0 }}
              >
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                <line x1="12" y1="6" x2="16" y2="6"></line>
                <line x1="12" y1="10" x2="16" y2="10"></line>
              </svg>
              Class Setup
            </button>
            
            <div style={{
              height: '1px',
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              margin: '4px 0',
            }}></div>
            
            
            <button
              onClick={() => {
                document.getElementById('hero-image-upload').click();
                setShowSettingsMenu(false);
                setShowFeedbackDropdown(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                padding: '14px 20px',
                backgroundColor: 'transparent',
                color: '#000',
                border: 'none',
                fontSize: '0.95rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
              Change Background Image
            </button>
            
            <div style={{
              height: '1px',
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              margin: '4px 0',
            }}></div>
            
            
            <button
              onClick={() => {
                navigate('/account-settings');
                setShowSettingsMenu(false);
                setShowFeedbackDropdown(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                padding: '14px 20px',
                backgroundColor: 'transparent',
                color: '#000',
                border: 'none',
                fontSize: '0.95rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              Account Settings
            </button>
            
            <div style={{
              height: '1px',
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              margin: '4px 0',
            }}></div>
            
            
            <button
              onClick={() => {
                window.open('https://list.myhomi.app', '_blank');
                setShowSettingsMenu(false);
                setShowFeedbackDropdown(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                padding: '14px 20px',
                backgroundColor: 'transparent',
                color: '#000',
                border: 'none',
                fontSize: '0.95rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
              </svg>
              Go to Homi List app
            </button>
            
            <div style={{
              height: '1px',
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              margin: '4px 0',
            }}></div>
            
            {/* Feedback button with dropdown */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowFeedbackDropdown(!showFeedbackDropdown)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  width: '100%',
                  padding: '14px 20px',
                  backgroundColor: 'transparent',
                  color: '#000',
                  border: 'none',
                  fontSize: '0.95rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                </svg>
                <span style={{ flex: 1 }}>Feedback</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{
                    transform: showFeedbackDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              
              {/* Feedback dropdown menu */}
              {showFeedbackDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '20px',
                  right: '20px',
                  marginTop: '4px',
                  backgroundColor: 'rgba(255, 255, 255, 0.98)',
                  borderRadius: '8px',
                  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                  overflow: 'hidden',
                  zIndex: 1002
                }}>
                  <button
                    onClick={() => openFeedbackModal('Bug Report')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: 'transparent',
                      color: '#000',
                      border: 'none',
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease',
                      textAlign: 'left'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <span>🐞</span>
                    <span>Bug Report</span>
                  </button>
                  
                  <button
                    onClick={() => openFeedbackModal('Feature Request')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: 'transparent',
                      color: '#000',
                      border: 'none',
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease',
                      textAlign: 'left'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <span>💡</span>
                    <span>Feature Request</span>
                  </button>
                  
                  <button
                    onClick={() => openFeedbackModal('General Question')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: 'transparent',
                      color: '#000',
                      border: 'none',
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease',
                      textAlign: 'left'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <span>🙋</span>
                    <span>General Question</span>
                  </button>
                </div>
              )}
            </div>
            
            <div style={{
              height: '1px',
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              margin: '4px 0',
            }}></div>
            
            <button
              onClick={() => {
                handleLogout();
                setShowSettingsMenu(false);
                setShowFeedbackDropdown(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                padding: '14px 20px',
                backgroundColor: 'transparent',
                color: '#000',
                border: 'none',
                fontSize: '0.95rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
              Logout
            </button>
          </div>
        )}
      </div>

      {/* Hero section with background image and greeting text */}
      <div className="image-container" style={{
        position: 'relative',
        width: '100%',
        height: isMobile ? '228px' : '300px',  // 228px (40% less) on mobile, 300px on desktop
        overflow: 'hidden',
        marginBottom: !isMobile ? '40px' : '0'
      }}>
        {/* Main background image - shows Dubai skyline */}
        <div className="background-image" style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: isMobile ? 'center center' : 'center center',  // Center the skyline view
          backgroundRepeat: 'no-repeat',
          filter: 'brightness(0.9)'  // Slightly darken for better text contrast
        }}></div>

        {/* Gradient fade to gray at bottom of image - smoother transition to content below */}
        <div className="gradient-overlay" style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: isMobile ? '120px' : '60px',
          background: 'linear-gradient(to bottom, rgba(245, 245, 245, 0), rgba(245, 245, 245, 1))',
          zIndex: 5,
          pointerEvents: 'none'
        }}></div>

        {/* Greeting text overlay - positioned over image */}
        <div className="greeting-section" style={{
          padding: isMobile ? '0' : '28px 24px',
          paddingTop: isMobile ? '0' : '50px',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          width: '100%',
          boxSizing: 'border-box',
          margin: 0,
          zIndex: 10
        }}>
          {/* Welcome message container - holds greeting text */}
          <div className="welcome-message" style={{
            position: 'relative',
            padding: isMobile ? '15px 16px' : '0',
            marginTop: isMobile ? '60px' : '20px'
          }}>
            {/* Dynamic time-based greeting text (Good morning/afternoon/evening/night) */}
            <div style={{
              color: '#ffffff',
              fontSize: isMobile ? '0.9rem' : '1.4rem',  // Larger greeting on desktop
              fontWeight: '500',
              marginBottom: isMobile ? '4px' : '8px',
              textShadow: isMobile ? '0 2px 8px rgba(0,0,0,0.9)' : '0 1px 3px rgba(0,0,0,0.5)'
            }}>
              {(() => {
                const hour = new Date().getHours();
                if (hour >= 5 && hour < 12) return 'Good morning';
                if (hour >= 12 && hour < 17) return 'Good afternoon';
                if (hour >= 17 && hour < 22) return 'Good evening';
                return 'Good night';
              })()}
            </div>
            {/* User name display - large text showing "Grahams!" */}
            <div style={{
              color: '#ffffff',
              fontSize: isMobile ? '1.5rem' : '3.5rem',  // Much larger name on desktop
              fontWeight: '700',
              letterSpacing: '-0.02em',
              textShadow: isMobile ? '0 2px 8px rgba(0,0,0,0.9)' : '0 2px 4px rgba(0,0,0,0.7)'
            }}>
              {firestoreUser?.firstName ? 
                (firestoreUser.firstName + '!') : // Use first name from Firestore if available
                firestoreUser?.displayName && firestoreUser.displayName.includes(' ') ? 
                  (firestoreUser.displayName.split(' ')[0] + '!') : // Get first name from display name
                  (currentUser?.email?.split('@')[0] + '!') // Fallback to email username
              }
            </div>
            
            {/* Welcome button */}
            <button
              className="welcome-button"
              onClick={() => setShowWelcomeModal(true)}
              style={{
                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                color: '#333',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '0.8rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(255, 215, 0, 0.3)',
                transition: 'all 0.2s ease',
                marginTop: isMobile ? '12px' : '16px'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(255, 215, 0, 0.4)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(255, 215, 0, 0.3)';
              }}
            >
              Welcome Guide
            </button>
          </div>
        </div>

      </div>


      {/* Main content area - three column layout on desktop, single column on mobile */}
      <div style={{
        display: !isMobile ? 'flex' : 'block',
        flexDirection: !isMobile ? 'row' : 'column',
        gap: !isMobile ? '30px' : '0',
        padding: !isMobile ? '0 40px' : '0',
        maxWidth: !isMobile ? '1600px' : '100%',
        margin: !isMobile ? '0 auto' : '0'
      }}>
        {/* Left column - Incoming events section (pending events) */}
        <div style={{
          flex: !isMobile ? '1' : 'none',
          width: isMobile ? '100%' : 'calc(33% - 20px)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Pending Events Section - always visible */}
          <div className="pending-events-container result-container" style={{
              width: '100%',
              margin: '0 auto',
              marginTop: isMobile ? '25px' : '0',
              marginBottom: isMobile ? '0' : '15px',
              position: 'relative',
              backgroundColor: '#f5f5f5',
              paddingTop: '0'
            }}>
              {/* Section header with "Incoming" title */}
              <div className="result-header" style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                marginTop: isMobile ? '-30px' : '0',
                marginBottom: isMobile ? '4px' : '16px',
                backgroundColor: 'transparent',
                borderBottom: 'none',
                padding: isMobile ? '4px 0' : '12px 0',
                position: 'relative',
                zIndex: 15,
              }}>
                {/* "Incoming" heading with white shadow for readability */}
                <h3 style={{
                  padding: '0',
                  paddingLeft: isMobile ? '24px' : '0',
                  margin: '0',
                  color: '#000000',
                  textShadow: '0 0 4px #ffffff, 0 0 2px #ffffff',
                  fontSize: isMobile ? '1.2rem' : '1.5rem',
                  fontWeight: isMobile ? '600' : '600'
                }}>Incoming</h3>
                <p style={{
                  padding: '0',
                  paddingLeft: isMobile ? '24px' : '0',
                  margin: isMobile ? '-10px 0 0 0' : '0',
                  color: '#666666',
                  fontSize: isMobile ? '0.85rem' : '0.9rem',
                  fontWeight: '400'
                }}>New events will appear here for your approval.</p>
              </div>
              {/* List container for pending event cards */}
              <div className="events-list" style={{
                maxHeight: 'none',
                width: '100%',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                alignItems: isMobile ? 'flex-start' : 'center',
                paddingLeft: isMobile ? '16px' : '0',
                paddingRight: isMobile ? '16px' : '0'
              }}>
                {/* Map through pending events and render SwipeableEvent cards */}
                {pendingEvents.length > 0 ? (
                  pendingEvents.map((event) => (
                    <IncomingCards
                      key={event.id}
                      event={event}
                      onAccept={acceptEvent}
                      onIgnore={ignoreEvent}
                      onEdit={() => setEventToEdit(event)}
                      getCategoryStyles={getCategoryStyles}
                      formatDateTime={formatDateTime}
                    />
                  ))
                ) : showEmptyState ? (
                  /* Empty state - You are all caught up */
                  <div style={{
                    background: '#238b45', // Vibrant royal green
                    color: '#ffffff',
                    borderRadius: '12px',
                    width: isMobile ? 'calc(100% - 48px)' : '100%',
                    boxSizing: 'border-box',
                    padding: isMobile ? '32px 16px' : '48px 24px',
                    boxShadow: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: isMobile ? '120px' : '160px',
                    marginBottom: '16px',
                    margin: isMobile ? '0 24px 16px 24px' : '0 0 16px 0',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    {/* Animated checkmark */}
                    <div style={{
                      marginBottom: '16px',
                      position: 'relative',
                      width: isMobile ? '48px' : '56px',
                      height: isMobile ? '48px' : '56px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {/* Ripple effect */}
                      <div style={{
                        position: 'absolute',
                        top: '0',
                        left: '0',
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        border: '2px solid rgba(255, 255, 255, 0.5)',
                        animation: 'ripple 1.5s ease-out 0.5s',
                        pointerEvents: 'none'
                      }} />
                      
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width={isMobile ? '48' : '56'}
                        height={isMobile ? '48' : '56'}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                          filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.2))',
                          animation: 'pulseScale 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
                          position: 'absolute',
                          zIndex: 2
                        }}
                      >
                        <circle 
                          cx="12" 
                          cy="12" 
                          r="10" 
                          strokeWidth="2"
                          fill="rgba(255, 255, 255, 0.1)"
                        />
                        <polyline 
                          points="8 12 11 15 16 10" 
                          strokeWidth="3"
                          fill="none"
                          style={{
                            strokeDasharray: '50',
                            strokeDashoffset: '50',
                            transformOrigin: 'center',
                            animation: 'checkPop 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.4s forwards'
                          }}
                        />
                      </svg>
                    </div>
                    <h3 style={{
                      fontSize: isMobile ? '1.2rem' : '1.4rem',
                      fontWeight: '600',
                      margin: '0 0 8px 0',
                      textAlign: 'center',
                      color: '#ffffff',
                      textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                    }}>
                      You're All Caught Up!
                    </h3>
                    <p style={{
                      fontSize: isMobile ? '0.9rem' : '1rem',
                      margin: '0',
                      textAlign: 'center',
                      color: '#ffffff'
                    }}>
                      No new events to review
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
        </div>
        
        {/* Middle column - Recent Activity section (accepted events) */}
        <div style={{
          flex: !isMobile ? '1' : 'none',
          width: isMobile ? '100%' : 'calc(33% - 20px)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Recent Activity container - shows last 10 accepted events */}
          <div className="recent-activity-container result-container" style={{
            width: '100%',
            margin: '0 auto',
            marginTop: isMobile ? '0' : '0',
            marginBottom: isMobile ? '0' : '15px',
            padding: '0',
            paddingTop: '0',
            position: 'relative',
            backgroundColor: '#f5f5f5',
            zIndex: 10
          }}>
            {/* Section header with "Recent Activity" title and tabs */}
            <div className="result-header" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: isMobile ? '-15px' : '0',
              marginBottom: isMobile ? '4px' : '16px',
              backgroundColor: '#f5f5f5',
              borderBottom: 'none',
              padding: isMobile ? '4px 0' : '12px 0',
              position: 'relative',
              zIndex: 15
            }}>
              {/* Dynamic heading based on mobile tab or desktop view */}
              <h3 style={{
                padding: '0',
                paddingLeft: isMobile ? '24px' : '0',
                margin: '0',
                color: '#000000',
                textShadow: '0 0 4px #ffffff, 0 0 2px #ffffff',
                fontSize: isMobile ? '1.2rem' : '1.5rem',
                fontWeight: isMobile ? '600' : '600',
                position: 'relative',
                zIndex: 20
              }}>
                {isMobile && activeMobileTab === 'emails' ? 'Seesaw Emails' : 'Recent Activity'}
              </h3>
              
              {/* Mobile tabs - only show on mobile */}
              {isMobile && (
                <div style={{ display: 'flex', marginRight: '24px' }}>
                  <style>
                    {`
                      .tab-button {
                        position: relative;
                        transition: all 0.5s ease-in-out;
                        overflow: hidden;
                      }

                      .tab-button:after {
                        content: '';
                        position: absolute;
                        bottom: -7px;
                        left: 0;
                        right: 0;
                        height: 3px;
                        background-color: var(--primary-color);
                        transform: scaleX(0);
                        transition: transform 0.3s ease;
                      }

                      .tab-button.active:after {
                        transform: scaleX(1);
                      }
                    `}
                  </style>
                  <button
                    className={`tab-button ${activeMobileTab === 'events' ? 'active' : ''}`}
                    onClick={() => setActiveMobileTab('events')}
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: activeMobileTab === 'events' ? 'var(--primary-color)' : '#666',
                      padding: '8px 12px',
                      fontSize: '15px',
                      fontWeight: activeMobileTab === 'events' ? '600' : '500',
                      transition: 'color 0.3s ease',
                      marginRight: '4px'
                    }}
                  >
                    Events
                  </button>
                  
                  {!isChild && !isDriver && (
                    <button
                      className={`tab-button ${activeMobileTab === 'emails' ? 'active' : ''}`}
                      onClick={() => setActiveMobileTab('emails')}
                      style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: activeMobileTab === 'emails' ? 'var(--primary-color)' : '#666',
                        padding: '8px 12px',
                        fontSize: '15px',
                        fontWeight: activeMobileTab === 'emails' ? '600' : '500',
                        transition: 'color 0.3s ease'
                      }}
                    >
                      Emails
                    </button>
                  )}
                </div>
              )}
            </div>
            
            {/* Events list shows for desktop always, and for mobile when events tab is active */}
            {(!isMobile || activeMobileTab === 'events') && (
              <div className="events-list" style={{
                maxHeight: 'none',
                width: '100%',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                paddingLeft: isMobile ? '16px' : '0',
                paddingRight: isMobile ? '16px' : '0',
                paddingBottom: isMobile ? '30px' : '0' // Extra padding on mobile for bottom nav
              }}>
                {recentActivity.length > 0 ? (
                  /* Map through activity items and render RecentActivityList */
                  <div style={{ 
                    backgroundColor: 'transparent',
                    borderRadius: '0',
                    padding: '0',
                    width: '100%'
                  }}>
                    {recentActivity.map((activity) => (
                      <RecentActivityList
                        key={activity.id}
                        event={activity}
                        onEdit={() => setEventToEdit(activity)}
                        onDelete={deleteAcceptedEvent}
                        getCategoryStyles={getCategoryStyles}
                        formatDateTime={formatDateTime}
                        isMobile={isMobile}
                      />
                    ))}
                  </div>
                ) : (
                  <div style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    color: '#666',
                    fontSize: '1rem'
                  }}>
                    <p>No recent activity</p>
                  </div>
                )}
              </div>
            )}
            
            {/* TLDR content for mobile emails tab - hidden for child users */}
            {isMobile && activeMobileTab === 'emails' && !isChild && !isDriver && (
              <div style={{
                width: '100%',
                boxSizing: 'border-box',
                paddingLeft: '16px',
                paddingRight: '16px'
              }}>
                {tldrs.length > 0 ? (
                  <TLDRContent 
                    tldrs={tldrs} 
                    duplicationMetadata={tldrDuplicationMetadata}
                    isMobile={true} 
                    formatTimestamp={formatTimestamp} 
                  />
                ) : (
                  <div style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    color: '#666',
                    fontSize: '1rem'
                  }}>
                    <p>No Seesaw email summaries yet</p>
                    <p style={{ fontSize: '0.9rem', marginTop: '10px' }}>
                      Import your emails to see TL;DR summaries here
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Right column - TLDR section - hidden for child users */}
        {!isMobile && tldrs.length > 0 && !isChild && !isDriver && (
          <div style={{
            flex: '1',
            width: 'calc(33% - 20px)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <TLDRSection 
              tldrs={tldrs} 
              duplicationMetadata={tldrDuplicationMetadata}
              isMobile={false} 
              formatTimestamp={formatTimestamp} 
            />
          </div>
        )}
      </div>


      {/* Hidden file input for hero image upload */}
      <input
        type="file"
        id="hero-image-upload"
        style={{ display: 'none' }}
        accept="image/*"
        onChange={handleHeroImageUpload}
      />

      {/* Welcome Modal */}
      {showWelcomeModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1001,
            padding: '20px',
            boxSizing: 'border-box'
          }}
          onClick={() => setShowWelcomeModal(false)}
        >
          <div 
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '480px',
              width: '100%',
              maxHeight: '85vh',
              overflowY: 'auto',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
              position: 'relative',
              margin: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setShowWelcomeModal(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                padding: '4px'
              }}
            >
              ×
            </button>
            
            {/* Modal content */}
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <h2 style={{ 
                color: '#333', 
                marginBottom: '6px',
                fontSize: '22px',
                fontWeight: '700'
              }}>
                Welcome to Homi 👋
              </h2>
              <p style={{ 
                color: '#666', 
                fontSize: '15px',
                marginBottom: '0',
                fontWeight: '400'
              }}>
                Let's get your calendar working for the whole family.
              </p>
            </div>
            
            <div style={{ color: '#555', lineHeight: '1.6', fontSize: '15px' }}>
              
              {/* Quick Setup Section */}
              <div style={{ marginBottom: '22px' }}>
                <h3 style={{ 
                  color: '#333', 
                  fontSize: '17px', 
                  fontWeight: '600', 
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{ fontSize: '16px' }}>⚡</span>
                  Quick Setup
                </h3>
                <p style={{ marginBottom: '12px', color: '#555' }}>
                  A few easy steps to get organized:
                </p>
                
                <div style={{ textAlign: 'left' }}>
                  <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <span style={{ fontSize: '16px', minWidth: '20px' }}>👨‍👩‍👧‍👦</span>
                    <div>
                      <strong>Set up your family</strong> – Add your partner, nanny, or grandparents.
                      <div style={{ color: '#888', fontSize: '12px', marginTop: '2px' }}>
                        Click on gear icon on Home.
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <span style={{ fontSize: '16px', minWidth: '20px' }}>📤</span>
                    <div>
                      <strong>Connect your email</strong> – Homi uses AI to pull events and summaries from official school emails (Seesaw & Arbor School).
                      <button
                        onClick={() => {
                          setShowWelcomeModal(false);
                          navigate('/upload?tab=email');
                        }}
                        style={{
                          marginTop: '8px',
                          padding: '8px 20px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '16px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'block'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#0056b3';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#007bff';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        Try now
                      </button>
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: '0', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <span style={{ fontSize: '16px', minWidth: '20px' }}>📆</span>
                    <div>
                      <strong>Sync your Google Calendar</strong>
                      <button
                        onClick={() => {
                          setShowWelcomeModal(false);
                          navigate('/upload?tab=gcal');
                        }}
                        style={{
                          marginTop: '8px',
                          padding: '8px 20px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '16px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'block'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#0056b3';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#007bff';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        Try now
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Top Features Section */}
              <div style={{ marginBottom: '16px' }}>
                <h3 style={{ 
                  color: '#333', 
                  fontSize: '17px', 
                  fontWeight: '600', 
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{ fontSize: '16px' }}>✨</span>
                  Top Features
                </h3>
                
                <div style={{ textAlign: 'left' }}>
                  <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <span style={{ fontSize: '16px', minWidth: '20px' }}>📥</span>
                    <div>
                      <strong>Smart event creation</strong> – just type naturally or upload PDFs (ie birthday invites) to auto-create events
                      <button
                        onClick={() => {
                          setShowWelcomeModal(false);
                          navigate('/upload?tab=paste');
                        }}
                        style={{
                          marginTop: '8px',
                          padding: '8px 20px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '16px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'block'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#0056b3';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#007bff';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        Try now
                      </button>
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <span style={{ fontSize: '16px', minWidth: '20px' }}>📅</span>
                    <div>
                      <strong>Everything in one place</strong> – School, family, and work calendars together
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <span style={{ fontSize: '16px', minWidth: '20px' }}>🔔</span>
                    <div>
                      <strong>Helpful reminders</strong> – Automatically creates reminders before events so you never forget!
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: '0', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <span style={{ fontSize: '16px', minWidth: '20px' }}>💬</span>
                    <div>
                      <strong>WhatsApp-ready</strong> – Easily share or receive weekly summaries
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div style={{ textAlign: 'center', marginTop: '18px' }}>
              <button
                onClick={() => setShowWelcomeModal(false)}
                style={{
                  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                  color: '#333',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(255, 215, 0, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(255, 215, 0, 0.3)';
                }}
              >
                Got it! Let's start ✨
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '1.5rem',
                fontWeight: '600',
                color: '#333'
              }}>Send Feedback</h2>
              <button
                onClick={() => setShowFeedbackModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleFeedbackSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  color: '#333',
                  marginBottom: '8px'
                }}>
                  Feedback Type
                </label>
                <input
                  type="text"
                  value={feedbackForm.type}
                  readOnly
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    backgroundColor: '#f8f9fa',
                    color: '#6c757d',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  color: '#333',
                  marginBottom: '8px'
                }}>
                  Your Email
                </label>
                <input
                  type="email"
                  value={feedbackForm.email}
                  onChange={(e) => setFeedbackForm({...feedbackForm, email: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#007bff'}
                  onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  color: '#333',
                  marginBottom: '8px'
                }}>
                  Subject
                </label>
                <input
                  type="text"
                  value={feedbackForm.subject}
                  onChange={(e) => setFeedbackForm({...feedbackForm, subject: e.target.value})}
                  required
                  placeholder="Brief description of your feedback"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#007bff'}
                  onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                />
              </div>

              <div style={{ marginBottom: '25px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  color: '#333',
                  marginBottom: '8px'
                }}>
                  Message
                </label>
                <textarea
                  value={feedbackForm.message}
                  onChange={(e) => setFeedbackForm({...feedbackForm, message: e.target.value})}
                  required
                  placeholder="Please provide details about your feedback..."
                  rows={5}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#007bff'}
                  onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                />
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  onClick={() => setShowFeedbackModal(false)}
                  style={{
                    padding: '12px 24px',
                    border: '2px solid #6c757d',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    backgroundColor: 'transparent',
                    color: '#6c757d',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#6c757d';
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#6c757d';
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '12px 24px',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    backgroundColor: '#007bff',
                    color: 'white',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#007bff'}
                >
                  Send Feedback
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
    </>
  );
}

// Component to display email body with AI-powered formatting
const EmailBodyDisplay = ({ emailBody, emailId, tldrId }) => {
  const [formattedContent, setFormattedContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const formatEmailWithAI = async () => {
      if (!emailBody) {
        setFormattedContent(`Email body not available. EmailId: ${emailId}`);
        setIsLoading(false);
        return;
      }

      try {
        // First clean the email content
        const cleanedContent = cleanEmailContent(emailBody);
        
        // Create a prompt for AI to format the email
        const prompt = `Please format this email content with proper paragraph breaks. Add line breaks where appropriate to make it easy to read. Keep the original content but add formatting. Return ONLY the formatted text with paragraph breaks (use \n\n for paragraph breaks):

${cleanedContent}`;

        // Since we can't directly call AI here, we'll use a smart regex-based approach
        // that mimics AI-like paragraph detection
        let formatted = cleanedContent;
        
        // First, check if the email starts with a duplicate header/title
        // Look for pattern where first line contains part of the second line
        const lines = formatted.split('\n');
        if (lines.length > 1) {
          const firstLine = lines[0].trim();
          const secondLine = lines[1].trim();
          
          // Check if first line is a header containing part of the content
          // Examples: "ELLEN SAWYER: Good afternoon, Inspire..." or just "Good afternoon, Inspire..."
          if (firstLine && secondLine && (
            // First line contains a colon and part of second line
            (firstLine.includes(':') && secondLine && firstLine.toLowerCase().includes(secondLine.substring(0, 20).toLowerCase())) ||
            // First line ends with ellipsis or quotes
            firstLine.endsWith('...') || firstLine.endsWith('"') ||
            // First line contains "Good morning/afternoon" and so does second line
            (firstLine.match(/Good\s+(morning|afternoon|evening|night)/i) && secondLine.match(/Good\s+(morning|afternoon|evening|night)/i)) ||
            // First line is all caps or mostly caps (likely a header)
            (firstLine.match(/^[A-Z\s:,"']+$/) || firstLine.match(/^[A-Z][A-Z\s]+:/))
          )) {
            // Remove the first line
            formatted = lines.slice(1).join('\n');
          }
        }
        
        // Additional cleanup for any remaining header patterns
        formatted = formatted
          // Remove lines that are just email headers with colons
          .replace(/^[A-Z][A-Z\s]*:\s*$/gm, '')
          // Remove empty quotes
          .replace(/^""\s*$/gm, '')
          
          // Smart paragraph detection patterns
          // Handle greetings properly - only add break after the comma
          .replace(/(Good\s+(morning|afternoon|evening|night)),\s*/gi, '$1,\n\n')
          .replace(/(Dear\s+[^,]+),\s*/gi, '$1,\n\n')
          .replace(/(Hi\s+[^,]+),\s*/gi, '$1,\n\n')
          .replace(/(Hello\s+[^,]+),\s*/gi, '$1,\n\n')
          
          // Handle exclamation headers (like "Inspire the Next Generation of Innovators!")
          .replace(/^([^.!?]+!)\s*(.)/gm, '$1\n\n$2')
          
          // Add break before common email closings
          .replace(/\s+(Sincerely,?)/gi, '\n\n$1')
          .replace(/\s+(Best\s+regards,?)/gi, '\n\n$1')
          .replace(/\s+(Best,?)/gi, '\n\n$1')
          .replace(/\s+(Thanks\s+for)/gi, '\n\n$1')
          .replace(/\s+(Thank\s+you)/gi, '\n\n$1')
          .replace(/\s+(Regards,?)/gi, '\n\n$1')
          
          // Add break before "If you have any questions"
          .replace(/\s+(If\s+you\s+have\s+any\s+questions)/gi, '\n\n$1')
          
          // Add break before signature lines
          .replace(/\s+(Miss\s+\w+)\s*$/gim, '\n\n$1')
          .replace(/\s+(Mr\.?\s+\w+)\s*$/gim, '\n\n$1')
          .replace(/\s+(Mrs\.?\s+\w+)\s*$/gim, '\n\n$1')
          .replace(/\s+(Ms\.?\s+\w+)\s*$/gim, '\n\n$1')
          .replace(/\s+(Dr\.?\s+\w+)\s*$/gim, '\n\n$1')
          
          // Handle paragraph breaks at natural boundaries
          .replace(/\.\s+([A-Z][a-z]+\s+[a-z]+)/g, '.\n\n$1')
          .replace(/\!\s+([A-Z][a-z]+\s+[a-z]+)/g, '!\n\n$1')
          
          // Clean up multiple breaks and trailing quotes
          .replace(/\n{3,}/g, '\n\n')
          .replace(/^"(.*)"\s*$/gm, '$1')
          .trim();

        setFormattedContent(formatted);
      } catch (err) {
        console.error('Error formatting email:', err);
        setError('Failed to format email');
        setFormattedContent(cleanEmailContent(emailBody));
      } finally {
        setIsLoading(false);
      }
    };

    formatEmailWithAI();
  }, [emailBody, emailId]);

  if (isLoading) {
    return (
      <div style={{
        marginTop: '8px',
        padding: '16px',
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        fontSize: '0.95rem',
        color: '#666',
        textAlign: 'center'
      }}>
        Formatting email content...
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: '8px',
        padding: '16px',
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        fontSize: '0.95rem',
        color: '#374151',
        lineHeight: '1.6',
        whiteSpace: 'pre-wrap',
        wordWrap: 'break-word',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}
    >
      {formattedContent}
    </div>
  );
};

// TLDR Content Component (without title header)
const TLDRContent = ({ tldrs, duplicationMetadata = { duplicatesFound: 0, details: [] }, isMobile, formatTimestamp }) => {
  const [showDuplicatesInfo, setShowDuplicatesInfo] = useState(false);
  const [expandedItems, setExpandedItems] = useState(new Set());
  
  // Check if we have any duplicates to show
  const hasDuplicates = duplicationMetadata && duplicationMetadata.duplicatesFound > 0;
  
  // Toggle expansion for a specific TLDR item
  const toggleExpansion = (tldrId) => {
    const newExpandedItems = new Set(expandedItems);
    if (expandedItems.has(tldrId)) {
      newExpandedItems.delete(tldrId);
    } else {
      newExpandedItems.add(tldrId);
    }
    setExpandedItems(newExpandedItems);
  };
  
  return (
    <div className="tldr-content-container">
      
      {/* Duplicates info panel */}
      {showDuplicatesInfo && hasDuplicates && (
        <div className="tldr-duplicates-info-panel" style={{
          backgroundColor: 'rgba(245, 245, 245, 0.8)',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '16px',
          fontSize: '0.9rem',
          border: '1px dashed #ddd'
        }}>
          <div className="tldr-duplicates-title" style={{ fontWeight: '600', marginBottom: '8px', color: '#555' }}>
            Duplicate TLDRs Hidden ({duplicationMetadata.duplicatesFound})
          </div>
          <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '0.85rem' }}>
            These similar emails were automatically merged to reduce clutter:
          </p>
          <div className="tldr-duplicates-list" style={{ maxHeight: '120px', overflowY: 'auto' }}>
            {duplicationMetadata.details.map(duplicate => (
              <div key={duplicate.id} className="tldr-duplicate-item" style={{ 
                fontSize: '0.8rem', 
                padding: '4px 0',
                borderBottom: '1px solid #eee'
              }}>
                <span className="tldr-duplicate-bullet" style={{ color: '#2A9D8F' }}>•</span> {duplicate.subject}
                <span className="tldr-duplicate-timestamp" style={{ color: '#999', marginLeft: '4px' }}>({formatTimestamp(duplicate.createdAt)})</span>
                {duplicate.reason === 'similar summary' && duplicate.similarityScore && (
                  <span className="tldr-duplicate-similarity-score" style={{ 
                    color: '#777', 
                    backgroundColor: 'rgba(42, 157, 143, 0.1)',
                    padding: '1px 4px',
                    borderRadius: '3px',
                    marginLeft: '6px',
                    fontSize: '0.7rem'
                  }}>
                    {Math.round(duplicate.similarityScore * 100)}% match
                  </span>
                )}
              </div>
            ))}
          </div>
          <button 
            onClick={() => setShowDuplicatesInfo(false)}
            style={{
              display: 'block',
              width: '100%',
              marginTop: '8px',
              padding: '6px',
              backgroundColor: 'transparent',
              border: '1px solid #ddd',
              borderRadius: '4px',
              color: '#666',
              fontSize: '0.8rem',
              cursor: 'pointer'
            }}
          >
            Hide Details
          </button>
        </div>
      )}
      
      {/* TLDR items list - inbox view */}
      <div className="tldr-items-list" style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
      }}>
        {tldrs.map((tldr) => {
          const isExpanded = expandedItems.has(tldr.id);
          
          return (
            <div 
              key={tldr.id} 
              className="tldr-inbox-item"
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                marginBottom: '2px',
                overflow: 'hidden',
                transition: 'all 0.2s ease'
              }}
            >
              {/* Condensed header - always visible */}
              <div 
                className="tldr-item-header"
                onClick={() => toggleExpansion(tldr.id)}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  borderBottom: isExpanded ? '1px solid #e0e0e0' : 'none',
                  backgroundColor: isExpanded ? '#f8f9fa' : 'transparent'
                }}
              >
                <div className="tldr-item-header-content" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  {/* Left side - Title and source */}
                  <div className="tldr-item-left-content" style={{ flex: 1, paddingRight: '12px' }}>
                    <h4 style={{
                      margin: '0 0 4px 0',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      color: '#333',
                      lineHeight: '1.3'
                    }}>
                      {tldr.emailSubject || tldr.subject || extractTitleFromSummary(tldr.summary) || 'Seesaw Update'}
                    </h4>
                    <div className="tldr-item-meta" style={{
                      fontSize: '0.8rem',
                      color: '#666',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span className="tldr-item-sender">{tldr.emailFrom || tldr.from || tldr.sender || 'Email'}</span>
                    </div>
                  </div>
                  
                  {/* Right side - Mark as read, delete button, and expand/collapse icon */}
                  <div className="tldr-item-actions" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        try {
                          deleteTLDR(tldr.id);
                        } catch (error) {
                          console.error('Error deleting TLDR:', error);
                        }
                      }}
                      style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: '#dc3545',
                        padding: '4px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                      title="Delete TLDR"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="m19 6-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6m5 0V4a2 2 0 0 1 2-2h0a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                    
                    {/* Expand/collapse chevron */}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#666"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                      }}
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                </div>
              </div>
              
              {/* Expanded content - only visible when expanded */}
              {isExpanded && (
                <div className="tldr-item-expanded" style={{
                  padding: '16px',
                  backgroundColor: '#ffffff',
                  position: 'relative'
                }}>
                  
                  {/* TLDR Summary */}
                  <div 
                    className="tldr-content"
                    style={{
                      margin: '-8px 0 12px 0',
                      fontSize: isMobile ? '0.85rem' : '0.95rem',
                      color: '#444',
                      lineHeight: '1.5'
                    }}
                    dangerouslySetInnerHTML={{
                      __html: formatMarkdownToHTML(tldr.summary)
                    }}
                  />
                  
                  {/* Expandable Full Email Body Section */}
                  {(tldr.emailBody || tldr.emailId) && (
                    <div 
                      onClick={() => {
                        const newExpandedEmails = new Set(expandedItems);
                        const emailKey = `email-${tldr.id}`;
                        if (expandedItems.has(emailKey)) {
                          newExpandedEmails.delete(emailKey);
                        } else {
                          newExpandedEmails.add(emailKey);
                        }
                        setExpandedItems(newExpandedEmails);
                      }}
                      style={{ 
                        marginTop: '16px', 
                        borderTop: '1px solid #e0e0e0', 
                        paddingTop: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: 'transparent',
                          border: 'none',
                          color: '#666',
                          padding: '4px 0',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          width: '100%',
                          textAlign: 'left'
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{
                            transform: expandedItems.has(`email-${tldr.id}`) ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease'
                          }}
                        >
                          <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                        View email
                      </div>
                      
                      {expandedItems.has(`email-${tldr.id}`) && (
                        <EmailBodyDisplay 
                          emailBody={tldr.emailBody}
                          emailId={tldr.emailId}
                          tldrId={tldr.id}
                        />
                      )}
                    </div>
                  )}
                  
                </div>
              )}
            </div>
          );
        })}
      </div>
      
    </div>
  );
};

// TLDR Section Component (with title header for desktop)
const TLDRSection = ({ tldrs, duplicationMetadata = { duplicatesFound: 0, details: [] }, isMobile, formatTimestamp }) => {
  const [showDuplicatesInfo, setShowDuplicatesInfo] = useState(false);
  
  // Check if we have any duplicates to show
  const hasDuplicates = duplicationMetadata && duplicationMetadata.duplicatesFound > 0;
  
  return (
  <div className="tldr-section-container" style={{
    backgroundColor: 'transparent',
    borderRadius: isMobile ? '0' : '12px',
    padding: isMobile ? '0' : '20px',
    boxShadow: 'none',
    marginBottom: isMobile ? '20px' : '20px',
    overflow: 'visible'
  }}>
    {/* TLDR Section header */}
    <div className="tldr-section-header" style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
      borderBottom: 'none',
      paddingBottom: '0',
    }}>
      <h3 style={{
        padding: '0',
        margin: '0',
        color: '#000000',
        fontSize: isMobile ? '1.2rem' : '1.5rem',
        fontWeight: '600',
      }}>
        Seesaw Emails
      </h3>
      
      {/* Show duplicate counter badge if there are any duplicates */}
      {hasDuplicates && (
        <button 
          onClick={() => setShowDuplicatesInfo(!showDuplicatesInfo)}
          style={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'rgba(42, 157, 143, 0.1)',
            color: '#2A9D8F',
            border: 'none',
            borderRadius: '12px',
            padding: '4px 8px',
            fontSize: '0.8rem',
            fontWeight: '500',
            cursor: 'pointer',
            gap: '4px'
          }}
        >
          <span className="tldr-section-duplicates-icon" style={{ fontSize: '1rem' }}>🔍</span>
          {duplicationMetadata.duplicatesFound} duplicate{duplicationMetadata.duplicatesFound !== 1 ? 's' : ''} hidden
        </button>
      )}
    </div>
    
    <TLDRContent 
      tldrs={tldrs} 
      duplicationMetadata={duplicationMetadata}
      isMobile={isMobile} 
      formatTimestamp={formatTimestamp} 
    />
  </div>
  );
};

export default New;